import { useEffect, useState, useCallback } from "react";
import { LayoutGrid, CalendarDays, Sparkles } from "lucide-react";
import { Header } from "@/components/Header";
import { EventCard } from "@/components/EventCard";
import { MonthGrid } from "@/components/MonthGrid";
import { RecentPhotos } from "@/components/RecentPhotos";
import { SubscribeCard } from "@/components/SubscribeCard";
import { apiFetch } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const HERO =
  "https://images.unsplash.com/photo-1590866249433-0310439fdab9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1900";

export default function Home() {
  const [events, setEvents] = useState([]);
  const [markerEvents, setMarkerEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [selectedDate, setSelectedDate] = useState(null);
  const [view, setView] = useState("grid");
  const [categories, setCategories] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (category !== "All") params.set("category", category);
    if (selectedDate) {
      params.set("start", selectedDate);
      params.set("end", selectedDate);
    }
    const data = await apiFetch(`/events?${params.toString()}`).catch(() => []);
    setEvents(data);
    setLoading(false);
  }, [query, category, selectedDate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (category !== "All") params.set("category", category);
    apiFetch(`/events?${params.toString()}`)
      .then(setMarkerEvents)
      .catch(() => {});
  }, [query, category]);

  useEffect(() => {
    apiFetch("/meta")
      .then((m) => setCategories(["All", ...m.categories]))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header query={query} onQueryChange={setQuery} />

      <section className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl">
          <img src={HERO} alt="Portland skyline" className="h-[300px] w-full object-cover sm:h-[380px]" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-end p-7 sm:p-12">
            <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-neutral-800">
              <Sparkles className="h-3.5 w-3.5 text-[#4D7C0F]" /> Curated by community leaders
            </span>
            <h1 className="max-w-2xl text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
              Every tech gathering in Portland, in one calendar.
            </h1>
            <p className="mt-3 max-w-xl text-base text-white/80">
              Meetups, workshops, hack nights and hallway conversations across the Silicon Forest.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`category-${c}`}
                onClick={() => setCategory(c)}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                  category === c
                    ? "border-neutral-900 bg-neutral-900 text-white"
                    : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 rounded-full border border-neutral-200 p-1">
            <button
              data-testid="view-grid"
              onClick={() => setView("grid")}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors duration-200 ${
                view === "grid" ? "bg-neutral-900 text-white" : "text-neutral-600"
              }`}
            >
              <LayoutGrid className="h-4 w-4" /> Grid
            </button>
            <button
              data-testid="view-calendar"
              onClick={() => setView("calendar")}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors duration-200 ${
                view === "calendar" ? "bg-neutral-900 text-white" : "text-neutral-600"
              }`}
            >
              <CalendarDays className="h-4 w-4" /> Calendar
            </button>
          </div>
        </div>

        <div className="mt-8">
          {view === "calendar" ? (
            loading ? (
              <Skeleton className="h-[720px] w-full rounded-2xl" />
            ) : (
              <MonthGrid events={markerEvents} />
            )
          ) : (
            <div>
            <p className="mb-5 text-sm font-semibold text-neutral-500" data-testid="results-count">
              {loading ? "Loading events…" : `${events.length} event${events.length === 1 ? "" : "s"}`}
              {selectedDate ? ` on ${selectedDate}` : ""}
            </p>

            {loading ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:gap-8">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i}>
                    <Skeleton className="aspect-[4/3] w-full rounded-2xl" />
                    <Skeleton className="mt-3 h-4 w-3/4" />
                    <Skeleton className="mt-2 h-3 w-1/2" />
                  </div>
                ))}
              </div>
            ) : events.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-neutral-300 p-14 text-center">
                <p className="text-base font-semibold text-neutral-900">No events match those filters</p>
                <p className="mt-1 text-sm text-neutral-500">Try another date or clear the category.</p>
              </div>
            ) : (
              <div
                className={`grid grid-cols-1 gap-6 sm:grid-cols-2 xl:gap-8 ${
                  view === "calendar" ? "lg:grid-cols-2" : "lg:grid-cols-3 xl:grid-cols-4"
                }`}
                data-testid="events-grid"
              >
                {events.map((e, i) => (
                  <EventCard
                    key={e.event_id}
                    event={e}
                    index={i}
                    onChange={(updated) =>
                      setEvents((prev) => prev.map((p) => (p.event_id === updated.event_id ? updated : p)))
                    }
                  />
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 pb-4 sm:px-6 lg:px-8">
        <SubscribeCard />
      </div>

      <RecentPhotos />

      <footer className="border-t border-neutral-200 py-10">
        <div className="mx-auto max-w-7xl px-4 text-sm text-neutral-500 sm:px-6 lg:px-8">
          Portland Tech Calendar · listings curated by invited community leaders.
        </div>
      </footer>
    </div>
  );
}
